import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareQuote, FileText, Users, ArrowRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../data/constants";

const StatCard = ({ icon: Icon, label, value, to, color = "text-orange-primary" }) => (
  <Link to={to} className="bg-white rounded-2xl shadow-card p-6 card-hover block" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon className={`${color} mb-3`} size={28} />
    <p className="text-sm text-gray-medium">{label}</p>
    <p className="font-display font-bold text-3xl text-gray-dark mt-1">{value}</p>
    <span className="inline-flex items-center gap-1 text-orange-primary text-sm font-semibold mt-3">
      Manage <ArrowRight size={14} />
    </span>
  </Link>
);

const AdminDashboardPage = () => {
  const { authedAxios } = useAuth();
  const [stats, setStats] = useState({ testimonials: 0, posts: 0, contactLeads: 0, dspLeads: 0, footTrafficLeads: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [testimonials, posts, contact, dsp, footTraffic] = await Promise.all([
          authedAxios({ url: `${API}/testimonials` }),
          authedAxios({ url: `${API}/admin/blog/posts` }),
          authedAxios({ url: `${API}/admin/leads/contact` }),
          authedAxios({ url: `${API}/admin/leads/dsp` }),
          authedAxios({ url: `${API}/admin/leads/foot-traffic` })
        ]);
        setStats({
          testimonials: testimonials.data.length,
          posts: posts.data.length,
          contactLeads: contact.data.length,
          dspLeads: dsp.data.length,
          footTrafficLeads: footTraffic.data.length
        });
      } catch (err) {
        setError("Could not load dashboard stats. Are you signed in?");
      }
    };
    load();
  }, [authedAxios]);

  const totalLeads = stats.contactLeads + stats.dspLeads + stats.footTrafficLeads;

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="font-display text-3xl font-bold text-gray-dark mb-2">Dashboard</h1>
      <p className="text-gray-medium mb-8">Quick overview of content and leads.</p>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard icon={MessageSquareQuote} label="Testimonials" value={stats.testimonials} to="/admin/testimonials" />
        <StatCard icon={FileText} label="Blog Posts" value={stats.posts} to="/admin/blog" />
        <StatCard icon={Users} label="Total Leads" value={totalLeads} to="/admin/leads" />
      </div>

      <div className="mt-10 bg-white rounded-2xl shadow-card p-6">
        <h2 className="font-display text-xl font-bold text-gray-dark mb-4">Lead breakdown</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-light rounded-lg p-4">
            <p className="text-sm text-gray-medium">Contact form</p>
            <p className="font-display font-bold text-2xl text-gray-dark">{stats.contactLeads}</p>
          </div>
          <div className="bg-gray-light rounded-lg p-4">
            <p className="text-sm text-gray-medium">DSP access requests</p>
            <p className="font-display font-bold text-2xl text-gray-dark">{stats.dspLeads}</p>
          </div>
          <div className="bg-gray-light rounded-lg p-4">
            <p className="text-sm text-gray-medium">Foot-traffic audits</p>
            <p className="font-display font-bold text-2xl text-gray-dark">{stats.footTrafficLeads}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
